const spacing = 11;
const offsetX = 0;
const offsetY = 15;
const borderWidth = 4;
const buttonSize = 61;
const regionPadding = 35;

const svg: SVGGraphicsElement = document.getElementById(
    "panel"
) as unknown as SVGGraphicsElement;

export function clearButtons(): void {
    svg.innerHTML = "";
}

interface Attributes {
    [key: string]: string | number;
}

interface CreateElOptions {
    under?: boolean;
}

export function createEl(
    tag: string,
    attributes: Attributes,
    opts?: CreateElOptions
): SVGElement {
    let el = document.createElementNS("http://www.w3.org/2000/svg", tag);
    for (let key in attributes) {
        const dashKey = key.replace(/([a-z])([A-Z])/g, "$1-$2").toLowerCase();
        el.setAttribute(dashKey, attributes[key].toString());
    }

    if (opts?.under) {
        svg.insertBefore(el, svg.firstChild);
    } else {
        svg.appendChild(el);
    }

    return el;
}

export function makeArrowButton(
    x: number,
    y: number,
    rotation: number,
    onClick?: () => void
): void {
    const { x: xx, y: yy } = buttonCoords(x, y);

    const button = createEl("polygon", {
        points: `${xx + buttonSize / 2},${yy + buttonSize / 4} ${xx},${
            yy + (3 * buttonSize) / 4
        } ${xx + buttonSize},${yy + (3 * buttonSize) / 4}`,
        fill: "transparent",
        stroke: "black",
        strokeWidth: 8,
        strokeLinejoin: "round",
        transform: `rotate(${rotation} ${xx + buttonSize / 2} ${
            yy + buttonSize / 2
        })`,
    });

    if (onClick) {
        button.style.cursor = "pointer";
        button.addEventListener("click", onClick);
    }
}

function buttonCoords(x: number, y: number): { x: number; y: number } {
    let xx = offsetX + spacing + x * (buttonSize + spacing);
    let yy = offsetY + spacing + y * (buttonSize + spacing);

    if (x > 9) xx += regionPadding;
    if (x > 16) xx += regionPadding;

    return {
        x: xx,
        y: yy,
    };
}

export function makeButton(opts: {
    x: number;
    y: number;
    w?: number;
    h?: number;
    t?: string;
    color?: string;
    borderColor?: string;
    borderWidth?: number;
    fontColor?: string;
    fontSize?: number;
    onClick?: () => void;
}): SVGRectElement {
    if (!opts.color) {
        opts.color = "transparent";
    }

    let { x: xx, y: yy } = buttonCoords(opts.x, opts.y);

    const ww = opts.w || buttonSize;
    const hh = opts.h || buttonSize;

    const xoff = opts.w ? (opts.w - buttonSize) / 2 : 0;
    const yoff = opts.h ? (opts.h - buttonSize) / 2 : 0;

    xx -= xoff;
    yy -= yoff;

    let button = createEl("rect", {
        x: xx,
        y: yy,
        width: ww,
        height: hh,
        rx: "10",
        ry: "10",
        fill: opts.color,
        stroke: opts.borderColor || "black",
        strokeWidth: opts.borderWidth || borderWidth,
    }) as SVGRectElement;

    if (opts.t) {
        const lines = opts.t.split("\n");

        let text = createEl("text", {
            x: xx,
            y: yy,
            width: ww,
            height: hh,
            textAnchor: "middle",
            fontSize: opts.fontSize || 12,
            fill: opts.fontColor || "black",
            fontFamily: "oswald",
            pointerEvents: "none",
        }) as SVGTextElement;

        for (let i = 0; i < lines.length; i++) {
            let tspan = createEl("tspan", {
                x: ww / 2 + xx,
                dy: "1em",
            }) as SVGTSpanElement;
            tspan.innerHTML = lines[i];
            text.appendChild(tspan);
        }

        // Center vertically
        const bbox = text.getBBox();
        const ty = yy + hh / 2 - bbox.height / 2;
        text.setAttribute("y", ty.toString());
    }

    if (opts.onClick) {
        button.style.cursor = "pointer";
        button.addEventListener("click", opts.onClick);
    }

    return button;
}

export function group(
    cornerA: SVGGraphicsElement,
    cornerB: SVGGraphicsElement,
    title: string,
    color: string
): void {
    const x1 = parseInt(cornerA.getAttribute("x")!);
    const y1 = parseInt(cornerA.getAttribute("y")!);
    const x2 = parseInt(cornerB.getAttribute("x")!);
    const y2 = parseInt(cornerB.getAttribute("y")!);
    const w1 = parseInt(cornerA.getAttribute("width")!);
    const h1 = parseInt(cornerA.getAttribute("height")!);
    const w2 = parseInt(cornerB.getAttribute("width")!);
    const h2 = parseInt(cornerB.getAttribute("height")!);
    const b1 = parseInt(cornerA.getAttribute("stroke-width")!);
    const b2 = parseInt(cornerB.getAttribute("stroke-width")!);

    const xx = Math.min(x1, x2);
    const yy = Math.min(y1, y2);
    const ww = Math.abs(x1 - x2) + Math.max(w1, w2) + b1 + b2;
    const hh = Math.abs(y1 - y2) + Math.max(h1, h2) + b1 + b2;

    const fh = 10;

    const border = createEl(
        "rect",
        {
            x: xx - b1 - spacing / 4 + 0.5,
            y: yy - b1 - spacing / 4 + 0.5 - fh,
            width: ww + spacing / 2 - 1,
            height: hh + spacing / 2 - 1 + fh,
            stroke: color,
            strokeWidth: 3,
            fill: "transparent",
            rx: 15,
            ry: 15,
        },
        { under: true }
    );

    const textBackground = createEl("rect", {
        fill: "gray",
    });

    let text = createEl("text", {
        x: xx + ww / 2,
        y: border.getAttribute("y")!,
        textAnchor: "middle",
        fontSize: "24",
        fill: color,
        fontFamily: "Oswald",
        dominantBaseline: "middle",
    }) as SVGTextElement;
    text.innerHTML = title;
    svg.appendChild(text);

    const bbox = text.getBBox();
    textBackground.setAttribute("x", (bbox.x - 5).toString());
    textBackground.setAttribute("y", bbox.y.toString());
    textBackground.setAttribute("width", (bbox.width + 10).toString());
    textBackground.setAttribute("height", bbox.height.toString());
}

export function join(
    a: SVGGraphicsElement,
    b: SVGGraphicsElement,
    color: string
): void {
    const aB = parseInt(a.getAttribute("stroke-width")!);
    const aX = parseInt(a.getAttribute("x")!);
    const aY = parseInt(a.getAttribute("y")!);
    const aW = parseInt(a.getAttribute("width")!);
    const aH = parseInt(a.getAttribute("height")!);

    const bB = parseInt(b.getAttribute("stroke-width")!);
    const bX = parseInt(b.getAttribute("x")!);
    const bY = parseInt(b.getAttribute("y")!);
    const bW = parseInt(b.getAttribute("width")!);
    const bH = parseInt(b.getAttribute("height")!);

    if (aX === bX) {
        const xx = aX - aB / 2;
        const ww = aW + aB;
        const yy = aY < bY ? aY + aH / 2 : bY + bH / 2;
        const hh = Math.abs(aY - bY) + Math.abs(aH - bH) + aB;
        createEl(
            "rect",
            {
                x: xx,
                y: yy,
                width: ww,
                height: hh,
                fill: color,
            },
            { under: true }
        );
    } else {
        const yy = aY - aB / 2;
        const hh = aH + aB;
        const xx = aX < bX ? aX + aW / 2 : bX + bW / 2;
        const ww = Math.abs(aX - bX) + Math.abs(aW - bW) + aB;
        createEl(
            "rect",
            {
                x: xx,
                y: yy,
                width: ww,
                height: hh,
                fill: color,
            },
            { under: true }
        );
    }
}

export function shrinkWrap(): void {
    const bbox = svg.getBBox();
    const w = bbox.x + bbox.width;
    const h = bbox.y + bbox.height;
    svg.setAttribute("viewBox", `0 0 ${w} ${h}`);
    svg.setAttribute("width", "100%");
    svg.removeAttribute("height");
}
